import { useAuthQuery } from '@/services/auth';
import { getExperimentVariation } from '@customerio/hydra-experiments';
import { useCallback, useMemo, useRef } from 'react';

const EXPERIMENTS = {
  discoverInApp: {
    variations: [
      { id: 'control', split: 0.5 },
      { id: 'treatment', split: 0.5 },
    ],
    criteria: 'account',
  },
} as const;

type ExperimentCriteria = 'account';

type Experiments = typeof EXPERIMENTS;
type ExperimentName = keyof Experiments;
type VariationIdOf<T extends ExperimentName> = Experiments[T]['variations'][number]['id'];

/**
 * `useExperiment` hook returns the variation ID for a specified experiment.
 *
 * This implementation aligns with the `experiments` service used in the Journeys UI:
 * https://github.com/customerio/ui/blob/main/app/services/experiments.ts.
 * Both the service and this hook use `hydra-experiments` to compute hashes
 * and determine experiment variations. The value is deterministic based on the identifier.
 *
 * Note: Centralizing experiment state would be ideal, exposing a full list
 * of experiments and hooks through `hydra-experiments` for unified state management or from hydra.
 *
 * @template T - The type of the experiment name.
 * @param {T} experimentName - The name of the experiment to retrieve a variation for.
 * @param {VariationIdOf<T>} [defaultVariationId] - An optional default variation ID.
 * @returns {VariationIdOf<T>} - The calculated or forced variation ID (non-production env) for the experiment.
 */
function useExperiment<T extends ExperimentName>(
  experimentName: T,
  defaultVariationId?: VariationIdOf<T>,
): VariationIdOf<T> {
  const forcedVariationId = getForcedExperiment(experimentName);
  const { data: auth } = useAuthQuery();

  const generateIdentifier = useCallback(
    (criteria: ExperimentCriteria) => {
      const account = auth?.account;
      if (criteria === 'account' && account != null) {
        return `${account.id}`;
      }

      return '0';
    },
    [auth],
  );

  const experimentRef = useRef(EXPERIMENTS[experimentName]);

  const variation = useMemo(() => {
    try {
      const identifier = generateIdentifier(experimentRef.current.criteria);
      return getExperimentVariation({
        name: experimentName,
        variations: [...experimentRef.current.variations],
        identifier,
      }) as VariationIdOf<T>;
    } catch (error) {
      if (defaultVariationId == null) {
        throw error;
      }

      return defaultVariationId;
    }
  }, [generateIdentifier, defaultVariationId, experimentName]);

  if (forcedVariationId != null) {
    return forcedVariationId;
  }

  return variation;
}

/**
 * Retrieves a forced experiment variation for development environments.
 * Updated via the debug widget in Fly.
 * @param experimentName - The name of the experiment.
 * @returns The variation ID of the experiment or null.
 * @see https://github.com/customerio/ui/blob/d4db7059c5102444b454f122432030b526570c4c/app/components/debug-widget/flags/index.ts#L68
 */
function getForcedExperiment<T extends ExperimentName>(experimentName: T): VariationIdOf<T> | null {
  if (__CIO_ENV__ === 'production') {
    return null;
  }

  const key = `experiment-${experimentName}`;
  const value = localStorage.getItem(key);

  return value as VariationIdOf<T>;
}

export { useExperiment };
